import * as React from 'react';
import { useState } from 'react';
import {
    List,
    Datagrid,
    FunctionField,
    Button,
    useNotify,
    EditButton,
    Confirm,
    useRefresh,
} from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import { useLocation } from 'react-router-dom';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../icon-button/IconButton.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { Switch } from '../switch/Switch.tsx';
import { EmptyBox } from "../empty-box/EmptyBox.tsx";
import { CategoryAddDialog } from '../dialogs/CategoryAddDialog.tsx';
import { RestaurantItemService } from "../../services/restaurantItem/restaurantItemService.ts";
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import * as Sentry from '@sentry/react';

//TODO: move styles to a separate file if possible

const LocationItemsTableActions = () => {
    const location = useLocation();
    const locationName = (location.state as { locationName?: string } | null)?.locationName;
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    
    const [open, setOpen] = React.useState(false);
    
    return (
        <Box
        sx={{
            // paddingTop: 'var(--space-72)',
            // paddingBottom: 'var(--space-32)',
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
        }}>
            <Box
                sx={{
                    paddingTop: 'var(--space-72)',
                    paddingBottom: 'var(--space-32)',
                    flex: 1,
                    display: 'flex',
                    alignItems: 'start',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
                >
                <Typography variant="h5" sx={{
                    paddingLeft: 'var(--space-24)',
                    fontWeight: 700,
                    fontSize: 'var(--text-xxl)',
                }}>
                    {t('resources.itemsManagement')}
                </Typography>
                <Divider
                    variant="middle"
                    sx={{
                        width: '460px',
                        alignSelf: 'stretch',
                        my: 'var(--space-24)',
                        borderTopWidth: 1,
                        borderColor: 'var(--color-secondary)',
                    }}
                    />

                <Typography variant="h5" sx={{
                    paddingLeft: 'var(--space-24)',
                    fontWeight: 700,
                    fontSize: 'var(--text-lg)',
                }}>
                    {locationName}
                </Typography>
            </Box>
            <Box
                sx={{
                    paddingTop: 'var(--space-72)',
                    paddingBottom: 'var(--space-32)',
                    width: '460px',
                    display: 'flex',
                    alignItems: 'end',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
                >
                <Button
                    style={{
                        fontSize: 'var(--text-xs)',
                        backgroundColor: 'transparent',
                        color: 'var(--color-primary)',
                        fontWeight: 700,
                        mt: '15px'
                        
                    }}
                    onClick={() => {
                        navigate(`/locationManagement/${id}/categories`, {
                            state: { locationName: locationName }
                        })
                    }
                }>
                    {t('locationItems.category').toLocaleUpperCase()}
                </Button>
                <Divider
                    variant="middle"
                    sx={{
                        width: '460px',
                        alignSelf: 'stretch',
                        my: 'var(--space-16)',
                        borderTopWidth: 1,
                        borderColor: 'var(--color-secondary)',
                    }}
                    />
                <IconButton icon={<AddIcon />} label={t('locationItems.addItems').toLocaleUpperCase()}
                    onClick={() => navigate(`/locationsManagement/${id}/items/create`)} />
            </Box>
            <CategoryAddDialog open={open} onClose={() => setOpen(false)} />
        </Box>
    );
};

/**
 * Visible or hidden - which is not what the availability switch beside it does.
 *
 * Availability greys the item out in the app and blocks the tap; the customer
 * still sees it. This takes it off the menu altogether. Two switches because
 * they are two decisions, and one control doing both would leave whoever ticks
 * it with no way to say which they meant.
 *
 * On is the good state in both columns, matching the locations table: checked
 * means the customer can see it.
 */
const MenuItemVisibilityToggle = ({ record }: { record: any }) => {
    const { t } = useTranslation();
    const notify = useNotify();
    const refresh = useRefresh();
    const [saving, setSaving] = React.useState(false);
    const isHidden = !!record.isHidden;

    const handleToggle = async (_: any, checked: boolean) => {
        // checked === visible to customers; unchecked === hidden.
        setSaving(true);
        try {
            await new RestaurantItemService().updateHiddenStatus(record.id, !checked);
            notify(t('common.saved'), { type: 'success' });
            refresh();
        } catch (error: any) {
            notify(error?.response?.data?.error ?? t('common.error'), { type: 'error' });
            Sentry.captureException(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
                checked={!isHidden}
                color="primary"
                size="small"
                disabled={saving}
                onChange={handleToggle}
            />
            <Typography sx={{ fontSize: 'var(--text-xxs)' }}>
                {isHidden
                    ? t('locationItems.visibility.hidden')
                    : t('locationItems.visibility.visible')}
            </Typography>
        </Stack>
    );
};

export const LocationItemsTable = () => {
    const refresh = useRefresh();
    const { t } = useTranslation();
    const { id } = useParams();
    const [switchStates, setSwitchStates] = useState<{ [key: number]: boolean }>({});
    const restaurantItemService = new RestaurantItemService();
    const notify = useNotify();
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [toDelete, setToDelete] = React.useState<number | null>(null);

    const lockUnLockItem = (itemId: number, currentState: boolean) => async () => {

        setSwitchStates(prev => ({
            ...prev,
            [itemId]: !currentState
        }));

        try {
            await restaurantItemService.lockUnlockItem(itemId);
        } catch (error) {
            setSwitchStates(prev => ({
                ...prev,
                [itemId]: currentState
            }));
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            Sentry.captureException(error);
        }
    }
    const resource = 'locationsManagement/' + id + '/items'
    return (
        <Box>
            <LocationItemsTableActions />
            <List
                title={t("resources.itemsManagement")}
                actions={false}
                filter={{ restaurantId: id }}
                empty={
                    <EmptyBox
                        title="No items found"
                        description="There are no items yet."
                        resource={resource}
                    />
                }
                perPage={25}
                resource="itemsManagement"
                sx={{
                    '& .RaList-main': { px: 4 },
                    '& .RaList-content': {
                        border: '1px solid var(--color-secondary)',
                        borderRadius: 'var(--radius-6)',
                    },
                    '& .RaDatagrid-tbody tr:last-of-type td': {
                        borderBottom: 'none',
                    },
                    '& th.MuiTableCell-root': {
                        paddingLeft: 'var(--space-24)',
                        py: 'var(--space-16)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& td.MuiTableCell-root': {
                        py: 'var(--space-24)',
                        paddingLeft: 'var(--space-24)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& .MuiTablePagination-root': {
                        '& .MuiSelect-select': {
                            paddingRight: '32px !important',
                            paddingLeft: '12px !important',
                        },
                        '& .MuiSelect-icon': {
                            right: '8px',
                        }
                    }
                }}
            >
                <Datagrid
                    bulkActionButtons={false}
                    rowClick={false}
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            fontWeight: 700,
                            letterSpacing: 0.2,
                        },
                    }}
                >
                    <FunctionField
                        sortable={false}
                        label={t('locationItems.columns.id')}
                        render={(record: any) => (record?.id ?? '')}
                    />
                    <FunctionField
                        sortable={false}
                        label={t('locationItems.columns.name')}
                        render={(record: any) => (record?.name ?? '')}
                    />
                    <FunctionField
                        sortable={false}
                        label={t('locationItems.columns.category')}
                        render={(record: any) => (record?.category ?? '').trim().split(' ')[0]}
                    />
                    <FunctionField
                        sortable={false}
                        label={t('locationItems.columns.price')}
                        render={(record: any) => (record?.price ?? '').trim().split(' ')[1]+' €'}
                    />
                    <FunctionField
                        sortable={false}
                        label={t('locationItems.columns.availability')}
                        render={(record: any) => {
                            const originalStatus: boolean = record.isAvailable;

                            const currentStatus = switchStates[record.id] !== undefined
                                ? switchStates[record.id]
                                : originalStatus;

                            // if (!(record.id in switchStates)) {
                            //     setSwitchStates(prev => ({
                            //         ...prev,
                            //         [record.id]: originalStatus
                            //     }));
                            // }

                            // const currentStatus = switchStates[record.id] || originalStatus;


                            return (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Switch
                                        checked={currentStatus}
                                        color="primary"
                                        size="small"
                                        onChange={() => {
                                            lockUnLockItem(record.id, currentStatus)();
                                        }}
                                    />
                                </Stack>
                            );
                        }}
                    />
                    <FunctionField
                        sortable={false}
                        label={t('locationItems.columns.visibility')}
                        render={(record: any) => (
                            <MenuItemVisibilityToggle record={record} />
                        )}
                    />
                    <FunctionField
                        label=""
                        sortable={false}
                        render={record => (
                            <>
                                <EditButton icon={<EditIcon />} label="" sx={{
                                    color: 'var(--color-primary)',
                                    minWidth: 'unset !important',
                                    padding: 0,
                                }}
                                    to={`/locationsManagement/${id}/items/${record.id}`}
                                />
                                <IconButton
                                    icon={<DeleteOutlineIcon />}
                                    color="var(--color-text-primary)"
                                    onClick={() => {
                                        setConfirmOpen(true);
                                        setToDelete(record.id);
                                    }}
                                />
                            </>
                        )}
                    />
                </Datagrid>
            </List>
            <Confirm
                isOpen={confirmOpen}
                title={t('locationItems.confirmDeletion.confirmDeletionTitle',)}
                content={t('locationItems.confirmDeletion.confirmDeletionBody')}
                confirm={t('common.delete')}
                cancel={t('common.cancel')}
                onConfirm={async () => {
                    try {
                        await restaurantItemService.deleteItem(toDelete!)
                        notify(t('common.saved'), { type: 'success' });
                        setConfirmOpen(false);
                        setToDelete(null);
                        refresh();
                    }
                    catch (error) {
                        notify(error.response.data.error, { type: 'error' });
                        console.error(error.response.data.error);
                        Sentry.captureException(error);
                        setConfirmOpen(false);
                        setToDelete(null);
                    }
                }}
                onClose={() => {
                    setConfirmOpen(false);
                    setToDelete(null);
                }}
            />
        </Box>
    )
};
